import { MapPin, Phone, Mail, ArrowUpRight } from "lucide-react";
import Reveal, { SectionHeading } from "../components/Reveal";
import { useEnquiry } from "../enquiry-context";
import { CONTACT } from "../data";

export default function Contact() {
  const { openEnquiry } = useEnquiry();

  return (
    <section id="contact" className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        {/* Commitment */}
        <div className="mx-auto max-w-4xl text-center">
          <Reveal>
            <p className="eyebrow justify-center">Our Commitment</p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold uppercase leading-tight text-stone-900" data-testid="commitment-statement">
              Building long-term relationships with{" "}
              <span className="text-molten">reliable die-casting solutions</span>, consistent
              quality, competitive manufacturing and responsive service.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-stone-600">
              We welcome enquiries for new component development, regular production, customised
              aluminium castings and OEM requirements.
            </p>
          </Reveal>
        </div>

        {/* Contact grid */}
        <div className="mt-20 grid gap-10 lg:grid-cols-2 lg:gap-16 items-start">
          <div>
            <SectionHeading eyebrow="Contact" title={<>Talk to the works</>} />
            <div className="mt-9 space-y-5">
              <Reveal delay={0.1}>
                <div className="flex items-start gap-4" data-testid="contact-address-text">
                  <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-stone-300 bg-white text-amber-700">
                    <MapPin size={18} />
                  </span>
                  <div>
                    <p className="font-tech text-[10px] uppercase tracking-[0.22em] text-stone-500">Works address</p>
                    <p className="mt-1 text-stone-800">{CONTACT.address}, India</p>
                  </div>
                </div>
              </Reveal>
              <Reveal delay={0.16}>
                <div className="flex items-start gap-4">
                  <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-stone-300 bg-white text-amber-700">
                    <Phone size={18} />
                  </span>
                  <div>
                    <p className="font-tech text-[10px] uppercase tracking-[0.22em] text-stone-500">Enquiries</p>
                    <a href={`tel:${CONTACT.phone1}`} data-testid="contact-phone-1" className="mt-1 block text-stone-800 hover:text-amber-700 transition-colors">
                      {CONTACT.phone1}
                    </a>
                    <a href={`tel:${CONTACT.phone2}`} data-testid="contact-phone-2" className="mt-0.5 block text-stone-800 hover:text-amber-700 transition-colors">
                      {CONTACT.phone2}
                    </a>
                  </div>
                </div>
              </Reveal>
              <Reveal delay={0.22}>
                <div className="flex items-start gap-4">
                  <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-stone-300 bg-white text-amber-700">
                    <Mail size={18} />
                  </span>
                  <div>
                    <p className="font-tech text-[10px] uppercase tracking-[0.22em] text-stone-500">Email</p>
                    <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-text" className="mt-1 block break-all text-stone-800 hover:text-amber-700 transition-colors">
                      {CONTACT.email}
                    </a>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>

          <Reveal delay={0.15}>
            <div className="relative overflow-hidden rounded-2xl border border-amber-500/30 bg-[#14171C] p-8 lg:p-10 shadow-[0_30px_80px_rgba(20,23,28,0.25)]">
              <div className="glow-pulse pointer-events-none absolute -top-20 -right-20 h-64 w-64 rounded-full bg-amber-500/15 blur-[80px]" />
              <p className="font-tech text-[10px] uppercase tracking-[0.25em] text-amber-400">Start a conversation</p>
              <h3 className="mt-3 font-display text-3xl lg:text-4xl font-bold uppercase leading-none text-slate-100">
                Send your drawing. <br /> Get your component.
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-slate-400">
                Share your requirement and our team will respond with feasibility, development
                plan and manufacturing details.
              </p>
              <button
                data-testid="contact-cta-enquiry"
                onClick={() => openEnquiry("General Enquiry")}
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-amber-500 px-7 py-3.5 text-sm font-bold uppercase tracking-widest text-stone-900 hover:bg-amber-400 hover:shadow-[0_0_36px_rgba(217,160,43,0.45)] transition-all"
              >
                Send Enquiry <ArrowUpRight size={16} strokeWidth={2.5} />
              </button>
            </div>
          </Reveal>
        </div>

        {/* Map */}
        <Reveal delay={0.1}>
          <div className="relative mt-14 overflow-hidden rounded-2xl border border-slate-800/80" data-testid="contact-map">
            <iframe
              data-testid="contact-map-iframe"
              title="SK Engineering Works location - SamayPur, Delhi"
              src="https://maps.google.com/maps?q=28.7146,77.1382(Samaypur%2C%20Delhi%20110042)&z=16&output=embed"
              className="h-[320px] w-full sm:h-[400px]"
              style={{ border: 0, filter: "grayscale(0.85) contrast(1.05)" }}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
            <div className="absolute left-4 top-4 max-w-[280px] rounded-xl border border-amber-500/30 bg-[#0F172A]/95 p-5 shadow-[0_20px_50px_rgba(0,0,0,0.4)] backdrop-blur-md">
              <p className="font-tech text-[10px] uppercase tracking-[0.22em] text-amber-400">Find us</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-200">
                Plot No-15 A, Lane No 14, Railway Road, SamayPur, Delhi 110042
              </p>
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=Plot+No-15+A+Lane+No+14+Railway+Road+SamayPur+Delhi+110042"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-map-directions-link"
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-amber-500 px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-[#080C14] hover:bg-amber-400 transition-colors"
              >
                Get Directions
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
